import React from "react";
import "normalize.css";
import { Navbar } from "../components/Navbar";
import { Sidebar } from "../components/Sidebar";
import { Map } from "../components/Map";
import { ExpirationContent } from "../components/ExpirationContent";
import config from "../../resources/config";
import { Response, LastLocation, FleetLogoAPI } from "./ResponseApi";
import * as Sentry from "@sentry/browser";
import moment, { Moment, Duration } from "moment";
import styles from "./Root.css";
import { ThumbsEnum, Rating } from "../components/Rating/Rating";
import { Thanks } from "../components/Thanks/Thanks";
import events from "../events";

require("../favicon.ico");

const HASH_LENGTH = 6;
interface Props {
  isScriptLoaded: boolean;
  isScriptLoadSucceed: boolean;
}

interface ETA {
  remainingDistance: string | undefined;
  estimatedDuration: Duration | undefined;
  estimatedTimeOfArrival: Moment | undefined;
}

export interface Vehicle {
  location: {
    lat: number;
    lng: number;
  };
  driverName: string | null;
  heading: number;
  licensePlate: string | null;
  ignitionOn: boolean;
  speed: number;
  brand: string | null;
  model: string | null;
  address: string | null;
  lastUpdatedTime: number | null;
}

interface Destination {
  lat: number;
  lng: number;
  address: string;
}

interface State {
  status: "default" | "expired";
  eta: ETA;
  googleScriptHasLoaded: boolean;
  locationHasLoaded: boolean;
  hasArrived: boolean;
  vehicle: Vehicle;
  destination: Destination;
  fleetName: string;
  conclusionTime: undefined | string;
  lastUpdatedTime: number;
  isRealTimeUpdated: boolean;
  fleetId: string;
  fleetLogoUrl: string | undefined;
  closedRatingModal: boolean;
  hasRating: boolean;
}
export class Root extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      status: "default",
      eta: {
        remainingDistance: undefined,
        estimatedDuration: undefined,
        estimatedTimeOfArrival: undefined
      },
      googleScriptHasLoaded: false,
      locationHasLoaded: false,
      hasArrived: false,
      vehicle: {
        location: { lat: 0, lng: 0 },
        driverName: null,
        heading: 0,
        licensePlate: null,
        ignitionOn: false,
        speed: 0,
        brand: null,
        model: null,
        address: null,
        lastUpdatedTime: null
      },
      destination: { lat: 0, lng: 0, address: "-" },
      fleetName: "",
      conclusionTime: undefined,
      lastUpdatedTime: 0,
      isRealTimeUpdated: true,
      fleetId: "",
      fleetLogoUrl: undefined,
      hasRating: false,
      closedRatingModal: false
    };
  }

  componentWillReceiveProps({ isScriptLoaded, isScriptLoadSucceed }: Props) {
    if (isScriptLoaded && !this.props.isScriptLoaded) {
      if (isScriptLoadSucceed) {
        this.setState({ googleScriptHasLoaded: true });
        this.getLastLocationRegularly();
        this.getETARegularly();
        setInterval(this.getETARegularly, config.getEtaIntervalInMillis);
        setInterval(
          this.getLastLocationRegularly,
          config.getLastLocationIntervalInMillis
        );
      }
    }

    if (this.state.status === "expired") {
      clearInterval();
    }
  }

  componentDidUpdate(_: Props, prevState: State) {
    if (prevState.fleetId !== this.state.fleetId) {
      this.getFleetLogo();
    }
  }

  getETARegularly = () => {
    if (!this.state.vehicle.location) return;

    const service = new google.maps.DistanceMatrixService();

    if (!this.state.locationHasLoaded) {
      setTimeout(
        this.getETARegularly,
        config.waitForLastLocationIntervalInMillis
      );
      return;
    }

    service.getDistanceMatrix(
      {
        origins: [
          `${this.state.vehicle.location.lat},${this.state.vehicle.location.lng}`
        ],
        destinations: [
          `${this.state.destination.lat},${this.state.destination.lng}`
        ],
        travelMode: google.maps.TravelMode.DRIVING,
        drivingOptions: {
          departureTime: moment().toDate()
        }
      },
      (
        response: google.maps.DistanceMatrixResponse,
        status: google.maps.DistanceMatrixStatus
      ) => {
        if (status === google.maps.DistanceMatrixStatus.OK) {
          const googleETA = response.rows[0].elements[0];

          const estimatedDurationInSeconds = googleETA.duration_in_traffic
            ? googleETA.duration_in_traffic.value
            : googleETA.duration.value;

          const remainingDistance = googleETA.distance.text.replace(".", ",");

          const now = moment();
          const estimatedDuration = moment.duration(
            estimatedDurationInSeconds,
            "seconds"
          );
          const estimatedTimeOfArrival = now.add(estimatedDuration);

          const eta = {
            estimatedDuration,
            remainingDistance,
            estimatedTimeOfArrival
          };

          this.setState({ eta });
        }
      }
    );
  };

  getLastLocationRegularly = () => {
    if (this.state.status === "expired") {
      return;
    }

    function buildRouteForPath(path: string) {
      return config.baseUrl + path;
    }

    const initRequestObject = {
      method: "GET",
      cacheable: false
    };

    const hash = window.location.search.slice(1);
    if (hash.length === HASH_LENGTH) {
      fetch(buildRouteForPath("dash/device/eta/" + hash), initRequestObject)
        .then(response => {
          if (response.status !== 200 && response.status !== 404) {
            return null;
          }
          return response.json();
        })
        .then(resp => this.parseApiResponse(resp as Response))
        .catch(e => {
          Sentry.captureException(e);
          this.setState({ status: "expired" });
        });
    } else {
      this.setState({ status: "expired" });
    }
  };

  parseApiResponse = (response: Response | null) => {
    if (!response) {
      return null;
    }

    if (response.message) {
      return this.setState({ status: "expired" });
    }

    const vehicle: Vehicle = {
      location: {
        lat: response.last_location.latitude,
        lng: response.last_location.longitude
      },
      licensePlate: response.vehicle.license_plate,
      driverName: response.driver.name,
      heading: response.last_location.heading,
      ignitionOn: response.last_location.ignition_on,
      speed: response.last_location.speed,
      brand: response.vehicle.brand,
      model: response.vehicle.model,
      address: response.last_location.address,
      lastUpdatedTime: response.last_location.time * 1000
    };

    const isRealTimeUpdated = this.isRealTimeUpdated(response.last_location);

    const hasRating = response.has_rating
      ? JSON.parse(response.has_rating)
      : false;

    this.setState({
      destination: JSON.parse(response.destination),
      hasArrived: !!response.conclusion_time,
      conclusionTime: response.conclusion_time,
      fleetName: response.fleet_name,
      vehicle,
      locationHasLoaded: true,
      lastUpdatedTime: response.last_location.time * 1000,
      isRealTimeUpdated,
      fleetId: response.fleet_id,
      hasRating
    });
  };

  getFleetLogo = () => {
    const url =
      config.baseUrl + `dash/fleet/logo?fleet_id=${this.state.fleetId}`;
    fetch(url)
      .then(response => {
        if (response.status !== 200) {
          return null;
        }
        return response.json();
      })
      .then((responseJSON: FleetLogoAPI | null) => {
        if (responseJSON) {
          /*
            Modern browser cache every url of image:
            So, to trick browser to make a new request, we change url.
          */
          this.setState({
            fleetLogoUrl: `${responseJSON.logo_url}?${moment().valueOf()}`
          });
        }
      })
      .catch(err => Sentry.captureException(err));
  };

  sendRating = (thumbs: ThumbsEnum, message: string | null) => {
    const hash = window.location.search.slice(1);
    const thumbsUp = thumbs === ThumbsEnum.UP ? true : false;
    const payload = {
      thumbsUp,
      message
    };
    if (hash) {
      const url = config.baseUrl + `dash/device/eta/${hash}/rating`;
      fetch(url, {
        method: "POST",
        headers: new Headers({
          "Content-type": "application/json"
        }),
        body: JSON.stringify(payload)
      })
        .then(response => {
          if (response.status !== 200) {
            return null;
          } else {
            return response.json();
          }
        })
        .then(_ => {
          events.rating.submitted(thumbsUp);
          this.setState({ hasRating: true });
        })
        .catch(err => Sentry.captureException(err));
    }
  };

  isRealTimeUpdated = (lastLocation: LastLocation) => {
    const THRESHOLD_ON_IN_MINUTES = 15;
    const THRESHOLD_OFF_IN_MINUTES = 40;

    const lastEventTimestampInMillis = lastLocation.time * 1000;

    const now = moment();
    const lastStatus = moment(lastEventTimestampInMillis);

    if (!now.isSame(lastStatus, "day")) {
      return false;
    }

    const delayValueMinutes = now.diff(lastStatus, "minutes");
    const threshold = lastLocation.ignition_on
      ? THRESHOLD_ON_IN_MINUTES
      : THRESHOLD_OFF_IN_MINUTES;

    return delayValueMinutes <= threshold;
  };

  handleModalClose = () => {
    this.setState({ closedRatingModal: true });
  };

  render() {
    if (this.state.status === "expired") {
      return (
        <div>
          <Navbar fleetName={this.state.fleetName} />
          <ExpirationContent />
        </div>
      );
    }

    return (
      <>
        {this.state.hasArrived &&
          !this.state.closedRatingModal &&
          (!this.state.hasRating ? (
            <Rating
              fleetName={this.state.fleetName}
              onSubmit={this.sendRating}
              onClose={this.handleModalClose}
            />
          ) : (
            <Thanks onClose={this.handleModalClose} />
          ))}
        <Navbar
          fleetName={this.state.fleetName}
          fleetLogoUrl={this.state.fleetLogoUrl}
        />
        <div className={styles.container}>
          <Map
            vehicle={this.state.vehicle}
            destination={this.state.destination}
            arrived={this.state.hasArrived}
            googleScriptHasLoaded={this.state.googleScriptHasLoaded}
            locationHasLoaded={this.state.locationHasLoaded}
            fleetName={this.state.fleetName}
            lastUpdatedTime={this.state.vehicle.lastUpdatedTime}
            isRealTimeUpdated={this.state.isRealTimeUpdated}
          />
          <Sidebar
            speed={this.state.vehicle.speed}
            fleetName={this.state.fleetName}
            ignitionOn={this.state.vehicle.ignitionOn}
            brand={this.state.vehicle.brand}
            model={this.state.vehicle.model}
            address={this.state.destination.address}
            driverName={this.state.vehicle.driverName}
            vehicleRegistrationPlate={this.state.vehicle.licensePlate}
            arrived={this.state.hasArrived}
            arrivalTime={
              typeof this.state.conclusionTime == "string"
                ? Number.parseInt(this.state.conclusionTime)
                : undefined
            }
            lastUpdatedTime={this.state.lastUpdatedTime}
            isRealTimeUpdated={this.state.isRealTimeUpdated}
            remainingDistance={this.state.eta.remainingDistance}
            estimatedDuration={this.state.eta.estimatedDuration}
            estimatedTimeOfArrival={this.state.eta.estimatedTimeOfArrival}
          />
        </div>
      </>
    );
  }
}
